import { makeAutoObservable, reaction, runInAction } from "mobx";
import { RDate } from "../../core";
import { ErrorBox, SelectableFieldController, TextFieldController, } from "../../form";
import { t, translate } from "../../I18n";
import { Availability, Consents, Email, FullName, RegexPhone, } from "../../ordering";
import { Tracking } from "../../tracking";
import { ConsentsFormController } from "./ConsentsFormController";
const MIN_PEOPLE = 1;
const MAX_PEOPLE = 30;
const DAY_OF_WEEK_TO_JS = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
};
const ALL_WEEKDAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];
// The Email/FullName/RegexPhone value objects are immutable, but ErrorBox holds
// a long-lived reference to each domain object and reads its `availability`
// reactively. These thin observable holders provide that stable, mutable seam.
class FullNameModel {
    constructor(initialValue) {
        this.value = initialValue;
        makeAutoObservable(this);
    }
    get availability() {
        return new FullName(this.value).availability;
    }
}
class EmailModel {
    constructor(initialValue) {
        this.value = initialValue;
        makeAutoObservable(this);
    }
    get availability() {
        return Email.create(this.value).availability;
    }
}
class PhoneModel {
    constructor(initialValue) {
        this.value = initialValue;
        makeAutoObservable(this);
    }
    get availability() {
        return RegexPhone.create(this.value).availability;
    }
}
class NumPeopleModel {
    constructor(initialValue) {
        this.value = initialValue;
        makeAutoObservable(this);
    }
    get parsed() {
        const trimmed = this.value.trim();
        return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
    }
    get availability() {
        const trimmed = this.value.trim();
        return Availability.boolean({
            NumPeopleMissing: trimmed.length === 0,
            NumPeopleInvalid: trimmed.length > 0 && this.parsed === null,
            NumPeopleOutOfRange: this.parsed !== null &&
                (this.parsed < MIN_PEOPLE || this.parsed > MAX_PEOPLE),
        });
    }
}
class TimeSlotModel {
    constructor() {
        this.value = null;
        makeAutoObservable(this);
    }
    get availability() {
        return Availability.boolean({ TimeSlotMissing: this.value === null });
    }
}
/**
 * Drives the customer-facing table-reservation widget on `/rezerwuj-stolik`.
 * Replaces the legacy PureScript `Restaumatic.Site.Components.Reservation`
 * widget: restaurant-scoped availability/time-slot fetching, the booking form,
 * and the marketing-consents sub-form.
 */
export class ReservationFormController {
    static create(params) {
        var _a, _b, _c, _d;
        const stored = params.storageManager.restore();
        const consents = Consents.fromStorageData(params.consentsStorageManager.restore());
        return new ReservationFormController({
            reservationService: params.reservationService,
            storageManager: params.storageManager,
            consentsStorageManager: params.consentsStorageManager,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            consents,
            fullNameModel: new FullNameModel((_a = stored === null || stored === void 0 ? void 0 : stored.fullName) !== null && _a !== void 0 ? _a : ""),
            emailModel: new EmailModel((_b = stored === null || stored === void 0 ? void 0 : stored.email) !== null && _b !== void 0 ? _b : ""),
            phoneModel: new PhoneModel((_c = stored === null || stored === void 0 ? void 0 : stored.phone) !== null && _c !== void 0 ? _c : ""),
            numPeopleModel: new NumPeopleModel((stored === null || stored === void 0 ? void 0 : stored.numPeople) !== undefined ? String(stored.numPeople) : ""),
            notes: (_d = stored === null || stored === void 0 ? void 0 : stored.info) !== null && _d !== void 0 ? _d : "",
        });
    }
    constructor(params) {
        this.timeSlotModel = new TimeSlotModel();
        this._restaurantSlug = null;
        this._disabledDates = {
            disabledWeekdays: [],
            excludedDates: [],
        };
        this._timeSlotOptions = [];
        this._slotUtcByLabel = {};
        this._enableMarketingConsents = false;
        this._franchiseName = null;
        this._status = { type: "Editing" };
        this._viewTracked = false;
        this.reservationService = params.reservationService;
        this.storageManager = params.storageManager;
        this.consentsStorageManager = params.consentsStorageManager;
        this.timezone = params.timezone;
        this.consents = params.consents;
        this.fullNameModel = params.fullNameModel;
        this.emailModel = params.emailModel;
        this.phoneModel = params.phoneModel;
        this.numPeopleModel = params.numPeopleModel;
        this._reservationDay = RDate.now(this.timezone).add(2, "hours").fullDate;
        this.fullName = new TextFieldController({
            initialValue: this.fullNameModel.value,
            onUpdate: (value) => {
                this.fullNameModel.value = value;
                this.persist();
            },
            errorBox: new ErrorBox({
                domainObjects: [this.fullNameModel],
                errorMessages: {
                    FullNameMissing: translate(t._common.form.errors.required),
                    InvalidFullName: translate(t.validations.invalid_full_name),
                },
            }),
        });
        this.email = new TextFieldController({
            initialValue: this.emailModel.value,
            onUpdate: (value) => {
                this.emailModel.value = value;
                this.persist();
            },
            errorBox: new ErrorBox({
                domainObjects: [this.emailModel],
                errorMessages: {
                    EmailMissing: translate(t._common.form.errors.required),
                    InvalidEmail: translate(t.validations.invalid_email),
                },
            }),
        });
        this.phone = new TextFieldController({
            initialValue: this.phoneModel.value,
            onUpdate: (value) => {
                this.phoneModel.value = value;
                this.persist();
            },
            errorBox: new ErrorBox({
                domainObjects: [this.phoneModel],
                errorMessages: {
                    PhoneMissing: translate(t._common.form.errors.required),
                    InvalidPhoneNumber: translate(t.validations.invalid_phone),
                },
            }),
        });
        this.numPeople = new TextFieldController({
            initialValue: this.numPeopleModel.value,
            onUpdate: (value) => {
                this.numPeopleModel.value = value;
                this.persist();
            },
            errorBox: new ErrorBox({
                domainObjects: [this.numPeopleModel],
                errorMessages: {
                    NumPeopleMissing: translate(t._common.form.errors.required),
                    NumPeopleInvalid: translate(t._common.form.errors.invalid_int),
                    NumPeopleOutOfRange: translate(t._common.validations.invalid_range, {
                        min: String(MIN_PEOPLE),
                        max: String(MAX_PEOPLE),
                    }),
                },
            }),
        });
        this.notes = new TextFieldController({
            initialValue: params.notes,
            onUpdate: () => this.persist(),
        });
        this.timeSlot = new SelectableFieldController({
            initialValue: null,
            onUpdate: (value) => {
                this.timeSlotModel.value = value;
            },
            onClear: () => {
                this.timeSlotModel.value = null;
            },
            errorBox: new ErrorBox({
                domainObjects: [this.timeSlotModel],
                errorMessages: {
                    TimeSlotMissing: translate(t._common.form.errors.required),
                },
            }),
        });
        this.consentsController = new ConsentsFormController({
            consents: this.consents,
        });
        reaction(() => this.consents.storageData, (data) => this.consentsStorageManager.store(data));
        makeAutoObservable(this);
    }
    persist() {
        var _a;
        this.storageManager.store({
            fullName: this.fullNameModel.value,
            email: this.emailModel.value,
            phone: this.phoneModel.value,
            numPeople: (_a = this.numPeopleModel.parsed) !== null && _a !== void 0 ? _a : 0,
            info: this.notes.value,
        });
    }
    get status() {
        return this._status;
    }
    get isSuccess() {
        return this._status.type === "Success";
    }
    get isSubmitting() {
        return this._status.type === "Submitting";
    }
    /** The single error line shown above the submit button, or null. */
    get submitError() {
        switch (this._status.type) {
            case "RestaurantClosed":
                return translate(t.activerecord.errors.models.table_reservation.restaurant_closed);
            case "ValidationError":
                return translate(t.orders.form.correct_validation_errors);
            case "NetworkError":
                return translate(t.orders.form.correct_validation_errors);
            default:
                return null;
        }
    }
    get reservationDay() {
        return this._reservationDay;
    }
    get disabledDates() {
        return this._disabledDates;
    }
    get timeSlotOptions() {
        return this._timeSlotOptions;
    }
    get enableMarketingConsents() {
        return this._enableMarketingConsents;
    }
    get franchiseName() {
        return this._franchiseName;
    }
    get canSubmit() {
        return (this.fullName.isValid &&
            this.email.isValid &&
            this.phone.isValid &&
            this.numPeople.isValid &&
            this.timeSlot.isValid &&
            this._restaurantSlug !== null &&
            !this.isSubmitting);
    }
    trackView() {
        if (this._viewTracked) {
            return;
        }
        this._viewTracked = true;
        Tracking.trackEvent({ tag: "ReservationWidgetView" });
    }
    async setRestaurantSlug(slug) {
        if (slug === this._restaurantSlug) {
            return;
        }
        runInAction(() => {
            this._restaurantSlug = slug;
        });
        await this.loadForRestaurant(slug);
    }
    async setReservationDay(day) {
        runInAction(() => {
            this._reservationDay = day;
        });
        await this.loadTimeSlots();
    }
    async loadForRestaurant(slug) {
        const [availability, formConfig] = await Promise.all([
            this.reservationService.getAvailability(slug),
            this.reservationService.getFormConfig(slug),
        ]);
        if (availability !== null) {
            const availableWeekdays = new Set(availability.availableWeekdays);
            const excludedDates = new Set(availability.excludedDates);
            runInAction(() => {
                this._disabledDates = {
                    disabledWeekdays: ALL_WEEKDAYS.filter((day) => !availableWeekdays.has(day)).map((day) => DAY_OF_WEEK_TO_JS[day]),
                    excludedDates: availability.excludedDates,
                };
                this._reservationDay = this.firstAvailableDay(this._reservationDay, availableWeekdays, excludedDates);
            });
        }
        if (formConfig !== null) {
            runInAction(() => {
                this._enableMarketingConsents = formConfig.enableMarketingConsents;
                this._franchiseName = formConfig.franchiseName;
            });
        }
        await this.loadTimeSlots();
    }
    async loadTimeSlots() {
        const slug = this._restaurantSlug;
        if (slug === null) {
            return;
        }
        const slots = await this.reservationService.getTimeSlots(slug, this._reservationDay);
        if (slots === null) {
            return;
        }
        // Slots come back as UTC instants; the option value is the local "HH:mm"
        // label (so the picker and E2E selectors read as clock times), and the
        // label→UTC map recovers the instant to submit.
        const slotUtcByLabel = {};
        const options = slots.map((utc) => {
            const label = RDate.fromUtcTimeString(utc, this.timezone).formatHHmm();
            slotUtcByLabel[label] = utc;
            return { value: label, label };
        });
        runInAction(() => {
            this._timeSlotOptions = options;
            this._slotUtcByLabel = slotUtcByLabel;
        });
        const first = options[0];
        if (first !== undefined) {
            this.timeSlot.setValue(first.value);
        }
    }
    firstAvailableDay(start, availableWeekdays, excludedDates) {
        if (availableWeekdays.size === 0) {
            return start;
        }
        let day = RDate.fromLocalTimeString(`${start}T00:00:00`, this.timezone);
        for (let i = 0; i < 366; i++) {
            const iso = day.fullDate;
            if (availableWeekdays.has(day.weekday) && !excludedDates.has(iso)) {
                return iso;
            }
            day = day.add(1, "days");
        }
        return start;
    }
    async submit() {
        var _a;
        this.fullName.blur();
        this.email.blur();
        this.phone.blur();
        this.numPeople.blur();
        this.timeSlot.blur();
        const slug = this._restaurantSlug;
        const numPeople = this.numPeopleModel.parsed;
        const slotLabel = this.timeSlotModel.value;
        const reservationDate = slotLabel !== null ? ((_a = this._slotUtcByLabel[slotLabel]) !== null && _a !== void 0 ? _a : null) : null;
        if (!this.canSubmit ||
            slug === null ||
            numPeople === null ||
            reservationDate === null) {
            return;
        }
        runInAction(() => {
            this._status = { type: "Submitting" };
        });
        const consents = this.consents.storageData;
        const result = await this.reservationService.createReservation({
            restaurantSlug: slug,
            fullName: this.fullNameModel.value,
            phone: this.phoneModel.value,
            email: this.emailModel.value,
            info: this.notes.value.trim() === "" ? null : this.notes.value,
            numPeople,
            reservationDate,
            subscribeEmail: consents.subscribeEmail,
            subscribePhone: consents.subscribePhone,
            subscribeRestaumaticEmail: consents.subscribeRestaumaticEmail,
            subscribeRestaumaticPhone: consents.subscribeRestaumaticPhone,
        });
        runInAction(() => {
            switch (result.type) {
                case "success":
                    this._status = { type: "Success" };
                    break;
                case "restaurantClosed":
                    this._status = { type: "RestaurantClosed" };
                    break;
                case "validationError":
                    this._status = { type: "ValidationError" };
                    break;
                case "networkError":
                    this._status = { type: "NetworkError" };
                    break;
            }
        });
    }
}
